
import {
  defineComponent,
  reactive,
  toRefs,
  onMounted,
  computed,
  watch,
  Ref
} from "vue";
import { useAuth, PermissionPayload } from "../../../modules/auth";
import { useToast } from "../../../modules/message";
import { formatDateTime } from "../../../modules/utils";
import {
  ClienteUsuarioPayload,
  TipoUsuario,
  TIPOS_USUARIO,
  useClienteUsuarioService
} from "../../../modules/business/cliente/clienteUsuarioBusiness";

interface ClienteUsuariosState {
  usuarios: Array<ClienteUsuarioPayload>;
  entity: ClienteUsuarioPayload;
  dialogAberto: boolean;
  filtroLogin: string;
  filtroNome: string;
  filtroTipo: TipoUsuario | null;
  primeiraLinha: number;
  stateReady: boolean;
  stateId: string;
  loadings: Array<Ref<boolean>>;
}

/**
 * Aba "Usuários" do cadastro do cliente. Cada usuário é salvo pelo próprio
 * diálogo, independente do botão Salvar do cliente. Sem idCliente trabalha
 * com o cliente do administrador logado.
 */
export default defineComponent({
  props: {
    idCliente: Number,
    parentLoading: Boolean
  },
  setup(props) {
    const novoUsuario = (): ClienteUsuarioPayload => ({
      login: "",
      nome: "",
      senha: "",
      tipo: "F",
      ativo: true
    });

    const state = reactive<ClienteUsuariosState>({
      usuarios: [],
      entity: novoUsuario(),
      dialogAberto: false,
      filtroLogin: "",
      filtroNome: "",
      filtroTipo: null,
      primeiraLinha: 0,
      stateReady: false,
      stateId:
        "state-" +
        Math.random()
          .toString(36)
          .substring(2),
      loadings: []
    });

    const amIAllowed = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => {
      return useAuth().amIAllowed(authority, permissionInfo);
    };

    const stateLoading = computed(() => {
      return state.loadings.length !== 0 || !!props.parentLoading;
    });

    const linhasPorPagina = 20;

    // o tipo R (único, da GovPass) não é oferecido em nenhum ponto da tela
    const tiposFiltro = TIPOS_USUARIO.filter((t) => t.tipo !== "R");

    const contemTexto = (valor?: string | null, filtro?: string) =>
      !filtro || (valor ?? "").toLowerCase().includes(filtro);

    const possuiFiltro = computed(
      () =>
        !!state.filtroLogin.trim() ||
        !!state.filtroNome.trim() ||
        !!state.filtroTipo
    );

    // a lista vem inteira do backend; os filtros sao locais e combinados (E)
    const usuariosFiltrados = computed(() => {
      const login = state.filtroLogin.trim().toLowerCase();
      const nome = state.filtroNome.trim().toLowerCase();
      return state.usuarios.filter(
        (u) =>
          contemTexto(u.login, login) &&
          contemTexto(u.nome, nome) &&
          (!state.filtroTipo || u.tipo === state.filtroTipo)
      );
    });

    watch(
      () => [state.filtroLogin, state.filtroNome, state.filtroTipo],
      () => (state.primeiraLinha = 0)
    );

    const isTransportador = computed(
      () => !!state.entity.idUsuario && state.entity.tipo === "C"
    );

    const isProprioUsuario = computed(
      () =>
        !!state.entity.idUsuario &&
        state.entity.idUsuario === useAuth().user?.value?.idUsuario
    );

    const isUsuarioR = computed(
      () => !!state.entity.idUsuario && state.entity.tipo === "R"
    );

    // R e C só aparecem para exibir um usuário que já é desse tipo (campo travado)
    const tiposDisponiveis = computed(() =>
      TIPOS_USUARIO.filter(
        (t) =>
          (t.tipo !== "R" || isUsuarioR.value) &&
          (t.tipo !== "C" || isTransportador.value)
      )
    );

    const { getDescricaoTipo, isLoginGovpass } = useClienteUsuarioService();

    const carregarUsuarios = (): Promise<void> => {
      const { data, loading, getUsuarios } = useClienteUsuarioService();
      state.loadings.push(loading);
      return getUsuarios(props.idCliente)
        .then(() => {
          state.usuarios = data.value ?? [];
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    const abrirUsuario = (usuario?: ClienteUsuarioPayload) => {
      state.entity = usuario ? { ...usuario, senha: "" } : novoUsuario();
      state.dialogAberto = true;
    };

    const fecharUsuario = () => {
      state.dialogAberto = false;
      state.entity = novoUsuario();
    };

    const salvarUsuario = () => {
      const { loading, saveUsuario } = useClienteUsuarioService();
      const payload: ClienteUsuarioPayload = {
        idUsuario: state.entity.idUsuario,
        login: state.entity.login?.trim(),
        nome: state.entity.nome?.trim(),
        senha: state.entity.senha ? state.entity.senha : null,
        tipo: state.entity.tipo,
        ativo: state.entity.ativo
      };
      state.loadings.push(loading);
      saveUsuario(props.idCliente, payload)
        .then(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          useToast().success("Usuário salvo com sucesso");
          fecharUsuario();
          carregarUsuarios();
        })
        .catch(() => {
          // erro já exibido pelo useApi; mantém o diálogo aberto para correção
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    onMounted(() => {
      carregarUsuarios().then(() => (state.stateReady = true));
    });

    return {
      stateLoading,
      usuariosFiltrados,
      possuiFiltro,
      tiposFiltro,
      linhasPorPagina,
      tiposDisponiveis,
      isTransportador,
      isUsuarioR,
      isProprioUsuario,
      amIAllowed,
      abrirUsuario,
      fecharUsuario,
      salvarUsuario,
      getDescricaoTipo,
      isLoginGovpass,
      formatDateTime,
      ...toRefs(state)
    };
  }
});
